/*
 * piq-a11y.js
 * Pacific IQ accessibility remediation for the Bedrock Sandals theme.
 * Audit: WCAG 2.2 AA, axe-core 4.10.2, 16 Sep 2026. Finding ids match the audit report.
 *
 * theme.js is the vendored Palo Alto bundle and is marked do-not-edit, so the
 * behaviour it is missing is added here instead. This file loads after it.
 *
 * Covers:
 *   F1  announcement bar - hidden Flickity slides stay out of the tab order
 *   F4  search overlay   - Escape closes it, focus returns, bogus ARIA removed
 *   F6  product carousel - off-screen slides stay out of the tab order
 *   F8  size guide drawer - modal semantics, focus containment, focus restore
 *   F12 mobile nav drawer - same, plus Escape
 *   A1  Zipify cart drawer - interim containment and focus restore
 */

(function () {
	'use strict'

	// Every entry excludes a negative tabindex. The size guide's backdrop is a
	// <button tabindex="-1">, and matching it is exactly why the theme drops
	// focus onto the backdrop instead of the dialog content (F8).
	var FOCUSABLE = [
		'a[href]:not([tabindex^="-"])',
		'button:not([disabled]):not([tabindex^="-"])',
		'input:not([disabled]):not([type="hidden"]):not([tabindex^="-"])',
		'select:not([disabled]):not([tabindex^="-"])',
		'textarea:not([disabled]):not([tabindex^="-"])',
		'summary:not([tabindex^="-"])',
		'[tabindex]:not([tabindex^="-"])',
	].join(',')

	var SUPPORTS_INERT = 'inert' in HTMLElement.prototype
	var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, LINK: 1, TEMPLATE: 1, NOSCRIPT: 1 }

	// The observers below watch busy subtrees (Flickity, Swiper, app injection),
	// so every handler is coalesced to one run per frame.
	function debounced(fn) {
		var queued = false
		return function () {
			if (queued) return
			queued = true
			var done = false
			var run = function () {
				if (done) return
				done = true
				queued = false
				fn()
			}
			// Background tabs pause requestAnimationFrame, so a frame-only
			// debounce would never run there and the fix would look unapplied.
			// The timer is the backstop; whichever fires first wins.
			requestAnimationFrame(run)
			setTimeout(run, 250)
		}
	}

	function isVisible(el) {
		return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
	}

	function focusablesIn(root) {
		return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), isVisible)
	}

	/* ---------------------------------------------------------------------
	 * Page inerting
	 * Walk from the dialog up to <body> and neutralise every sibling on the
	 * way. That is what removes the 61 / 97 / 114 controls the audit found
	 * still reachable behind each overlay.
	 * ------------------------------------------------------------------- */
	var inertRecords = []

	function inertOutside(dialog) {
		releaseInert()

		var node = dialog
		while (node && node !== document.body && node.parentElement) {
			var parent = node.parentElement
			var siblings = Array.prototype.slice.call(parent.children)

			for (var i = 0; i < siblings.length; i++) {
				var sib = siblings[i]
				if (sib === node || SKIP_TAGS[sib.tagName]) continue
				if (sib.hasAttribute('data-piq-inert')) continue

				inertRecords.push({
					el: sib,
					hadAriaHidden: sib.hasAttribute('aria-hidden') ? sib.getAttribute('aria-hidden') : null,
				})

				sib.setAttribute('data-piq-inert', '')
				if (SUPPORTS_INERT) {
					sib.inert = true
				} else {
					sib.setAttribute('aria-hidden', 'true')
				}
			}

			node = parent
		}
	}

	function releaseInert() {
		for (var i = 0; i < inertRecords.length; i++) {
			var rec = inertRecords[i]
			rec.el.removeAttribute('data-piq-inert')
			if (SUPPORTS_INERT) rec.el.inert = false

			if (rec.hadAriaHidden === null) {
				rec.el.removeAttribute('aria-hidden')
			} else {
				rec.el.setAttribute('aria-hidden', rec.hadAriaHidden)
			}
		}
		inertRecords = []
	}

	/* ---------------------------------------------------------------------
	 * Remember what the visitor last interacted with, so focus can go back
	 * there when an overlay closes. Capture phase, because the overlay may
	 * move focus before the click finishes.
	 * ------------------------------------------------------------------- */
	var lastInteractive = null

	function rememberTrigger(event) {
		var el = event.target instanceof Element ? event.target.closest(FOCUSABLE) : null
		if (el) lastInteractive = el
	}

	document.addEventListener('pointerdown', rememberTrigger, true)
	document.addEventListener('keydown', function (event) {
		if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') rememberTrigger(event)
	}, true)

	/* ---------------------------------------------------------------------
	 * Dialog controller
	 * ------------------------------------------------------------------- */
	var activeDialog = null

	function firstFocusTarget(dialog) {
		var preferred = dialog.querySelector(
			'[data-piq-autofocus], [data-drawer-close], .drawer__close-button, [data-close], [data-popup-close], .search-popdown__close'
		)
		if (preferred && isVisible(preferred)) return preferred

		var all = focusablesIn(dialog)
		return all.length ? all[0] : dialog
	}

	function openDialog(dialog, options) {
		if (activeDialog === dialog) return
		if (activeDialog) closeDialog(activeDialog, { restoreFocus: false })

		var opts = options || {}

		activeDialog = dialog
		dialog.__piqTrigger = opts.trigger || lastInteractive || null

		if (!dialog.hasAttribute('aria-modal')) {
			dialog.setAttribute('aria-modal', 'true')
			dialog.__piqAddedAriaModal = true
		}

		inertOutside(dialog)

		// Defer to the theme when it has already placed focus on something the
		// visitor can actually use. The size guide focuses its backdrop button,
		// which has tabindex="-1", so "inside the dialog" is not the test.
		var active = document.activeElement
		var placedWell = active && dialog.contains(active) && active !== dialog && active.matches(FOCUSABLE)

		placeFocus(dialog, placedWell)

		// The theme places its own focus on transitionend, roughly 400ms after the
		// open class lands, and the size guide sends it to the backdrop. Re-check
		// after the transition and correct it if it ended up somewhere unusable.
		;[450, 900].forEach(function (delay) {
			setTimeout(function () {
				if (activeDialog !== dialog) return
				var el = document.activeElement
				var ok = el && dialog.contains(el) && el !== dialog && el.matches(FOCUSABLE)
				if (!ok) placeFocus(dialog, false)
			}, delay)
		})
	}

	function placeFocus(dialog, alreadyPlaced) {
		if (alreadyPlaced) return
		var target = firstFocusTarget(dialog)
		if (!target.hasAttribute('tabindex') && !target.matches(FOCUSABLE)) {
			target.setAttribute('tabindex', '-1')
		}
		try {
			target.focus({ preventScroll: true })
		} catch (e) {
			target.focus()
		}
	}

	function closeDialog(dialog, options) {
		if (activeDialog !== dialog) return
		var opts = options || {}

		releaseInert()

		if (dialog.__piqAddedAriaModal) {
			dialog.removeAttribute('aria-modal')
			dialog.__piqAddedAriaModal = false
		}

		activeDialog = null

		var trigger = dialog.__piqTrigger
		dialog.__piqTrigger = null

		if (opts.restoreFocus !== false && trigger && document.contains(trigger) && isVisible(trigger)) {
			try {
				trigger.focus({ preventScroll: true })
			} catch (e) {
				trigger.focus()
			}
		}
	}

	function requestClose(dialog) {
		// Prefer the component's own close path so the theme's state stays consistent.
		if (typeof dialog.close === 'function') {
			try {
				dialog.close()
				return true
			} catch (e) {
				/* fall through to the close button */
			}
		}

		// Scoped to real controls. A bare [class*="close"] would match things
		// like a "closeout" badge inside a third-party widget.
		var button = dialog.querySelector(
			[
				'[data-drawer-close]',
				'.drawer__close-button',
				'[data-close]',
				'[data-popup-close]',
				'button[class*="close" i]',
				'button[aria-label*="close" i]',
			].join(',')
		)
		if (button) {
			button.click()
			return true
		}

		return false
	}

	/* Escape closes whichever overlay is open. The mobile nav drawer was the
	 * only overlay on the site that ignored Escape entirely (F12). */
	document.addEventListener('keydown', function (event) {
		if (event.key !== 'Escape' && event.key !== 'Esc') return
		if (!activeDialog) return

		if (requestClose(activeDialog)) {
			event.preventDefault()
			event.stopPropagation()
		}
	})

	/* Fallback containment for browsers without inert. */
	document.addEventListener('keydown', function (event) {
		if (SUPPORTS_INERT || event.key !== 'Tab' || !activeDialog) return

		var items = focusablesIn(activeDialog)
		if (!items.length) return

		var first = items[0]
		var last = items[items.length - 1]

		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault()
			last.focus()
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault()
			first.focus()
		}
	})

	/* ---------------------------------------------------------------------
	 * Watch the overlays the audit flagged.
	 * Each entry says how to find the element and how to tell it is open.
	 * ------------------------------------------------------------------- */
	var WATCHED = [
		// F8 size guide, F12 mobile nav, and the theme's other drawer-elements
		{ selector: 'drawer-element', isOpen: function (el) { return el.classList.contains('is-open') } },
		// F13 PIQ's own collection sort/filter drawer
		{ selector: 'l-drawer', isOpen: function (el) { return el.classList.contains('is-active') } },
		// A1 Zipify OneClickUpsell cart drawer (interim, vendor fix still needed)
		{ selector: '.ocu-cart-drawer', isOpen: function (el) { return el.classList.contains('ocu-visible') } },
	]

	var observed = new WeakSet()

	function watch(el, isOpen) {
		if (observed.has(el)) return
		observed.add(el)

		var wasOpen = isOpen(el)
		if (wasOpen) openDialog(el)

		new MutationObserver(function () {
			var nowOpen = isOpen(el)
			if (nowOpen === wasOpen) return
			wasOpen = nowOpen

			if (nowOpen) {
				// Let the theme's own transitionend focus handling run first.
				setTimeout(function () {
					if (isOpen(el)) openDialog(el)
				}, 0)
			} else {
				closeDialog(el)
			}
		}).observe(el, { attributes: true, attributeFilter: ['class'] })
	}

	function scan() {
		for (var i = 0; i < WATCHED.length; i++) {
			var entry = WATCHED[i]
			var found = document.querySelectorAll(entry.selector)
			for (var j = 0; j < found.length; j++) {
				watch(found[j], entry.isOpen)
			}
		}
	}

	/* ---------------------------------------------------------------------
	 * F4 - search overlay
	 * The theme sets aria-expanded on the search input whenever the overlay
	 * opens, but the input only carries role="combobox" when predictive
	 * search is enabled. With it off the attribute is not allowed on a plain
	 * input[type=search], which is the aria-allowed-attr violation.
	 * Escape also needs to close the overlay and hand focus back.
	 * ------------------------------------------------------------------- */
	function searchToggle() {
		return document.querySelector('search-popdown details[open] > summary[data-popdown-toggle]')
	}

	function cleanSearchInputAria() {
		var inputs = document.querySelectorAll('.search-form__input[aria-expanded]')
		for (var i = 0; i < inputs.length; i++) {
			var input = inputs[i]
			if (input.getAttribute('role') !== 'combobox') {
				input.removeAttribute('aria-expanded')
			}
		}
	}

	function initSearch() {
		var searchTrigger = null

		new MutationObserver(function () {
			if (document.body.classList.contains('search-opened')) {
				searchTrigger = searchToggle() || lastInteractive
				cleanSearchInputAria()
			} else {
				searchTrigger = null
			}
		}).observe(document.body, { attributes: true, attributeFilter: ['class'] })

		// The theme re-applies the attribute on each open, so clean it then too.
		document.addEventListener('focusin', function (event) {
			if (event.target && event.target.classList && event.target.classList.contains('search-form__input')) {
				cleanSearchInputAria()
			}
		})

		document.addEventListener('keydown', function (event) {
			if (event.key !== 'Escape' && event.key !== 'Esc') return
			if (!document.body.classList.contains('search-opened')) return

			var popdown = document.querySelector('search-popdown details[open]')
			if (!popdown) return

			var closeButton = popdown.querySelector('[data-popdown-close], .search-popdown__close')
			var toggle = popdown.querySelector('summary[data-popdown-toggle]')

			if (closeButton) {
				closeButton.click()
			} else if (toggle) {
				toggle.click()
			}

			// The theme blurs the active element on close (triggerPopdownClose), so
			// a single rAF restore gets undone. Re-assert past that.
			var restoreTo = toggle || searchTrigger
			if (restoreTo && document.contains(restoreTo)) {
				// Delays straddle the ~400ms close transition; the body class is still
				// 'search-opened' for most of it, so it cannot be used as the guard.
				;[0, 150, 450, 800].forEach(function (delay) {
					setTimeout(function () {
						if (document.activeElement === restoreTo) return
						if (document.querySelector('search-popdown details[open]')) return
						try {
							restoreTo.focus({ preventScroll: true })
						} catch (e) {
							restoreTo.focus()
						}
					}, delay)
				})
			}

			event.preventDefault()
			event.stopPropagation()
		})
	}

	/* ---------------------------------------------------------------------
	 * F1 and F6 - Flickity slides that are off screen keep their controls in
	 * the tab order. Links inside them are aria-hidden, so a screen reader
	 * announces nothing while focus sits on an invisible link.
	 * ------------------------------------------------------------------- */
	var CAROUSELS = [
		'[data-slider]',
		'.announcement__scale',
		'ticker-bar',
		'.l-cPCarousel',
		'[data-product-carousel]',
	].join(',')

	function syncSlideFocusability(carousel) {
		var slides = carousel.querySelectorAll('.flickity-slider > *, [data-slide]')

		for (var i = 0; i < slides.length; i++) {
			var slide = slides[i]
			var offScreen = !slide.classList.contains('is-selected') && slide.getAttribute('aria-hidden') === 'true'

			var controls = slide.querySelectorAll(FOCUSABLE)
			for (var j = 0; j < controls.length; j++) {
				var control = controls[j]

				if (offScreen) {
					if (!control.hasAttribute('data-piq-tabindex')) {
						control.setAttribute('data-piq-tabindex', control.getAttribute('tabindex') || '')
					}
					control.setAttribute('tabindex', '-1')
				} else if (control.hasAttribute('data-piq-tabindex')) {
					var original = control.getAttribute('data-piq-tabindex')
					if (original) {
						control.setAttribute('tabindex', original)
					} else {
						control.removeAttribute('tabindex')
					}
					control.removeAttribute('data-piq-tabindex')
				}
			}
		}
	}

	var carouselsSeen = new WeakSet()

	function initCarousels() {
		var carousels = document.querySelectorAll(CAROUSELS)

		for (var i = 0; i < carousels.length; i++) {
			if (carouselsSeen.has(carousels[i])) continue
			carouselsSeen.add(carousels[i])
			;(function (carousel) {
				syncSlideFocusability(carousel)

				new MutationObserver(
					debounced(function () {
						syncSlideFocusability(carousel)
					})
				).observe(carousel, {
					attributes: true,
					attributeFilter: ['class', 'aria-hidden'],
					subtree: true,
				})
			})(carousels[i])
		}
	}

	/* ---------------------------------------------------------------------
	 * F2 / F3 - the mega menu container carried role="button" alongside a
	 * duplicate role="navigation". Dropping the duplicate left a button
	 * wrapping focusable links (axe nested-interactive). The container is now
	 * roleless in the markup and the disclosure state belongs on the link that
	 * actually takes focus. The theme's custom element keeps writing those
	 * attributes onto itself, so mirror them across as they change.
	 * ------------------------------------------------------------------- */
	var disclosuresSeen = new WeakSet()

	function initDisclosures() {
		var items = document.querySelectorAll('hover-disclosure')

		for (var i = 0; i < items.length; i++) {
			if (disclosuresSeen.has(items[i])) continue
			disclosuresSeen.add(items[i])
			;(function (el) {
				var link = el.querySelector('[data-top-link]')
				if (!link) return

				var sync = function () {
					;['aria-expanded', 'aria-haspopup', 'aria-controls'].forEach(function (name) {
						if (el.hasAttribute(name)) {
							link.setAttribute(name, el.getAttribute(name))
							el.removeAttribute(name)
						}
					})
					if (el.getAttribute('role') === 'button') el.removeAttribute('role')
				}

				sync()
				new MutationObserver(sync).observe(el, {
					attributes: true,
					attributeFilter: ['aria-expanded', 'aria-haspopup', 'aria-controls', 'role'],
				})
			})(items[i])
		}
	}

	/* ---------------------------------------------------------------------
	 * F6 - controls in off-screen carousel slides stay in the tab order.
	 * The Flickity handling above keys off aria-hidden, which Swiper and the
	 * PIQ card carousels never set. This is geometry based instead: anything
	 * scrolled outside its own carousel viewport is taken out of the tab
	 * order, and put back when it scrolls in.
	 * ------------------------------------------------------------------- */
	var CAROUSEL_ROOTS = [
		'.swiper',
		'.flickity-viewport',
		'[data-slider]',
		'[class*="carousel"]',
		'[class*="Carousel"]',
	].join(',')

	var rootsSeen = new WeakSet()

	function neutralise(el, offScreen) {
		if (offScreen) {
			if (!el.hasAttribute('data-piq-offscreen')) {
				el.setAttribute('data-piq-offscreen', el.getAttribute('tabindex') || '')
			}
			el.setAttribute('tabindex', '-1')
		} else if (el.hasAttribute('data-piq-offscreen')) {
			var prev = el.getAttribute('data-piq-offscreen')
			if (prev) el.setAttribute('tabindex', prev)
			else el.removeAttribute('tabindex')
			el.removeAttribute('data-piq-offscreen')
		}
	}

	function watchCarouselRoot(root) {
		if (rootsSeen.has(root)) return
		// Only meaningful where the track actually overflows its viewport.
		if (root.scrollWidth <= root.clientWidth + 20) return
		rootsSeen.add(root)

		var io = new IntersectionObserver(
			function (entries) {
				entries.forEach(function (e) {
					neutralise(e.target, e.intersectionRatio < 0.5)
				})
			},
			{ root: root, threshold: [0, 0.5, 1] }
		)

		var register = function () {
			var bounds = root.getBoundingClientRect()
			root.querySelectorAll(FOCUSABLE).forEach(function (el) {
				if (!el.hasAttribute('data-piq-observed')) {
					el.setAttribute('data-piq-observed', '')
					io.observe(el)
				}
				// Settle it immediately by geometry. The observer keeps this
				// current as the carousel scrolls, but its first callback is
				// not guaranteed to arrive in a paused tab.
				var r = el.getBoundingClientRect()
				if (r.width || r.height) {
					neutralise(el, r.right < bounds.left + 4 || r.left > bounds.right - 4)
				}
			})
		}

		register()
		new MutationObserver(debounced(register)).observe(root, { childList: true, subtree: true })
	}

	function initCarouselRoots() {
		document.querySelectorAll(CAROUSEL_ROOTS).forEach(watchCarouselRoot)
	}

	/* ------------------------------------------------------------------- */
	function init() {
		scan()
		initDisclosures()
		initCarouselRoots()
		initSearch()
		initCarousels()

		// Overlays and carousels can be injected after load (Zipify, section
		// rendering, theme editor), so keep looking.
		new MutationObserver(
			debounced(function () {
				scan()
				initCarousels()
				initDisclosures()
				initCarouselRoots()
			})
		).observe(document.body, { childList: true, subtree: true })

		document.addEventListener('shopify:section:load', function () {
			scan()
			initCarousels()
		})
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init)
	} else {
		init()
	}
})()
